/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Dispatch, SetStateAction } from "react";
import { Stack, Center } from "@chakra-ui/react";
import { useForm, FormProvider } from "react-hook-form";
import { User, UserInfosCreateInput } from "types/User";
import { useAuth } from "src/shared/contexts/AuthContext";
import useTranslation from "next-translate/useTranslation";
import NameInput from "../Controls/Infos/NameInput";
import { Button } from "src/shared/components/Button";
import { SmileSignUpSteps } from "../..";
import * as GTagHelper from "src/utils/helpers/GTagHelper";
import { track } from "src/services/Segment/analytics";
import { useSentry } from "src/services/Sentry";

interface FormData {
  firstName: string;
  lastName: string;
  software: string;
  otherSoftware: string;
}

const convertToPayload = ({ software, otherSoftware, ...other }: FormData): Partial<UserInfosCreateInput> => {
  return {
    ...other,
    softwareName: software === "otherSoftware" ? otherSoftware : software,
  };
};

/**
 * Both array are used to render the radio and to fill them
 * if the user goes back to infos
 */
const softwareData: { name: "software"; value: string; text: string }[] = [
  { name: "software", value: "Ortholeader", text: "softwareFirstOption" },
  { name: "software", value: "Orthokis", text: "softwareSecondOption" },
  { name: "software", value: "Orhalis/Kitview", text: "softwareThirdOption" },
  { name: "software", value: "otherSoftware", text: "sourceElseOption" },
];

/**
 * Fill the values if the user goes back.
 * if are needed to handle the "autre" field
 */
const handleDefaultValues = (profile: User | undefined) => {
  const defaultValues: Partial<FormData> = {};

  if (profile !== undefined) {
    defaultValues.firstName = profile.infos?.firstName;
    defaultValues.lastName = profile.infos?.lastName;

    if (
      profile.infos?.softwareName !== undefined &&
      softwareData.map((elem) => elem.value).includes(profile.infos?.softwareName)
    ) {
      defaultValues.software = profile.infos?.softwareName;
    } else {
      defaultValues.software = "otherSoftware";
      defaultValues.otherSoftware = profile.infos?.softwareName;
    }
  }
  return defaultValues;
};

const SmileSignupInfosForm = ({ setStep }: { setStep: Dispatch<SetStateAction<SmileSignUpSteps>> }) => {
  const { t } = useTranslation();
  const [{ profile }, { updateUserInfos }] = useAuth();
  const formMethods = useForm<FormData>({
    mode: "onBlur",
    defaultValues: handleDefaultValues(profile),
  });
  const { handleSubmit, formState } = formMethods;
  const { log } = useSentry();

  const onSubmit = handleSubmit(async (values) => {
    let payload: Partial<UserInfosCreateInput> = {};

    // merge place values and form values
    payload = { ...payload, ...convertToPayload(values) };
    updateUserInfos(payload);

    try {
      GTagHelper.event({
        action: "FRONT | New SignUp | STEP 2 | Got User Infos",
        category: "SIGNUP",
        payload: { email: profile?.email },
      });
      if (typeof window.analytics !== "undefined")
        track("FRONT | New SignUp | STEP 2 | Got User Infos", {
          email: profile?.email,
        });
    } catch (e: any) {
      log(e, "error");
    }
    setStep(SmileSignUpSteps.PLANS_STEP);
  });

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit}>
        <Stack justify="center" align="center">
          <Stack spacing="88px" mb="55px" w={"100%"}>
            <NameInput />
            <Center>
              <Button type="submit" isLoading={formState.isSubmitting} isFullWidth={true}>
                {t("smile:pixSignup_goButton")}
              </Button>
            </Center>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default SmileSignupInfosForm;
